import * as Sentry from '@sentry/angular';
import { PassportData } from './day.dto';
import { CellData } from './excel-generation-data.dto';
import { RtsDay, RtsLoadingData, RtsVehicle } from './get-rts-result.dto';
import { Status } from './status.enum';
import { TransporterType } from './transporter-type.enum';

export type IsoZonedDateTime =
  `${number}-${string}-${string}T${string}:${string}:${string}${string}[${string}]`;

export interface SignEndOfDayRequest {
  signatureDateTime: IsoZonedDateTime;
  /**
   * Ключ идемпотентности логической подписи расписки (стабилен между онлайн-попыткой и повтором из
   * очереди синхронизации). Сервер по нему возвращает ранее созданный документ вместо создания
   * второго — защита от дубля при потерянном ответе сети.
   */
  idempotencyKey?: string;
  vehicles: {
    billOfLandingNumber: string; //ТТН
    billOfLandingDate: string;
    vehicleNumber: string;
    trailerNumber: string;
    loadingDate: string;
    loadedWeightKg: number;
  }[];
}

export interface Attribute {
  prefix: string;
  name: string;
  value: string;
  unit: string;
}

export interface ActionLog {
  action: string;
  datetime: string;
  parameters: Record<string, string | number>;
}

export interface BuyerData {
  address: string;
  id: string;
  inn: string;
  kpp: string;
  orgName: string;
  phone: string;
}

export interface LoadingPointData {
  regionName: string;
  address: string;
  districtId: number;
  regionId: number;
  hasScales: boolean;
  hasTrawls: boolean;
  hasTrailers?: boolean | null;
  heightLimits: 0;
  loadingMethod: string;
  maxTonPerDay: number;
  scaleLength: number;
  title: string;
  workFrom: string;
  workTo: string;
  owner?: {
    inn: string;
    name: string;
  };
}

export interface UnloadingPointData {
  regionName: string;
  address: string;
  districtId: number;
  regionId: number;
  fiasId: string;
  title: string;
}

export interface SellerData {
  id: number;
  inn: string;
  orgName: string;
  okpo: string;
  phone: string;
}

export interface ForwarderData {
  address: string;
  id: number;
  inn: string;
  orgName: string;
  phone: string;
  kpp: string;
  ogrnip?: string;
  email?: string;
  passport?: PassportData;
  okpo: string;
  bankDetails: string;
}

export interface ConsigneeData {
  inn: string;
  kpp: string;
  name: string;
  ogrn: string;
  address: string;
}

export interface OrderRegistry {
  completed: boolean;
  days: RtsDay[];
}

export interface UnloadedDeliveryInfo {
  unloadedWeight: number;
  unloadedDays: UnloadedDays[];
}

export interface UnloadedDays {
  unloadedDate: string;
  unloadedWeight: number;
  vehicles: UnloadedVehicleInfo[];
}

export interface UnloadedVehicleInfo {
  expeditorLoadedDate: null | string;
  goodsMainPropertyValue: number | string;
  goodsType: string;
  registrationNumber: string;
  trailerRegistrationNumber: string | null;
  ttnId: string;
  unloadedWeight: number;
}

/**
 * Доменная модель заявки экспедирования на устройстве. Помимо реквизитов сделки несёт {@code registry}
 * — реестр дней отгрузки с машинами (рейсами) и введёнными данными погрузки, вокруг которого строится
 * весь офлайн-цикл: правки, слияние с сервером и синхронизация. Правки идут через методы, возвращающие
 * {@code this}; статус заявки ({@code IN_WORK}/{@code COMPLETED}/{@code CLOSED} и др.) определяет, что
 * можно делать с днями (см. {@link Order#canProcessDay}).
 */
export class Order {
  buyer: BuyerData;
  createdDate: number;
  cropYear: number;
  consignee: ConsigneeData;
  transporterOrderId: string;
  transporterType?: TransporterType;
  /**
   * Перевозчик по заявке назначен (заявку взяли на рынке перевозок). Приходит вместе с реестром, а не
   * из строки заявки: назначение живёт в trucking, а колонка {@code transporterOrderId} осталась от
   * снятых внешних контуров и в новом потоке не заполняется.
   */
  carrierAssigned?: boolean;
  /** Наименование организации-перевозчика — экспедитору оно нужно: он координирует погрузку. */
  carrierName?: string;
  /**
   * Последнее известное от сервера состояние назначения перевозчика; хранится в локальной копии и без связи
   * не сбрасывается (в отличие от {@link #carrierAssigned}, который без свежих сведений снимается для показа).
   * По нему решается, можно ли начать день погрузки без связи: перевозка без назначенного перевозчика
   * запрещена, а отказаться от взятой заявки перевозчик не может, поэтому подтверждённое назначение не
   * устаревает.
   */
  carrierAssignmentConfirmed?: boolean;
  declarationOfConformityDate: number;
  declarationOfConformityNumber: string;
  distance: number;
  forwarder: ForwarderData;
  forwarderFee: number;
  shipmentDates: string[];
  freightAttributes: Attribute[];
  freightName: string;
  id: number;
  bargainId: number;
  loading: LoadingPointData;
  quarantineCertificate: boolean;
  deliveredWeightInKilograms: number;
  registry: OrderRegistry;
  requestedWeightInTons: number;
  sampling: boolean;
  seller: SellerData;
  status: Status;
  unloading: UnloadingPointData;
  updatedDate: number;
  version: number;
  contractName: string;
  // Клиентский журнал действий (генерация ТТН/ТРН/отчёта/РТС) — накапливается методами generate*Actions,
  // встроен в заказ и переносится при слиянии (mergeWithLocalOrder берёт log из локальной версии).
  log: ActionLog[];
  unloadedDeliveryInfo: UnloadedDeliveryInfo;

  constructor(data: Partial<Order>) {
    Object.assign(this, data);
  }

  /**
   * Совпадает ли день реестра с искомым идентификатором (номер строки РТС без разделителей '|').
   *
   * У дня-скелета номера нет (реестр ещё не наполнен перевозчиком) — и у наполненного дня он тоже
   * может отсутствовать, если его не прислал сервер перевозчика. Раньше `orderNumber.replace(...)`
   * вызывался прямо в предикате поиска, и один такой день ронял TypeError на весь реестр:
   * переставали работать поиск дня, добавление и правка машин. Пустые значения с обеих сторон
   * теперь не совпадают ни с чем: день без номера открыть нельзя, но остальной реестр работает.
   */
  private static isDay(day: RtsDay, dayId?: string | null): boolean {
    return !!day.orderNumber && !!dayId && day.orderNumber.replace(/\|/g, '') === dayId;
  }

  /**
   * Можно ли начать день погрузки: заявка в работе (поручение подписано) и перевозчик назначен. Начатый день —
   * начало перевозки, а перевозка без перевозчика запрещена.
   */
  public canStartDay(): boolean {
    return this.status === Status.IN_WORK && this.carrierAssignmentConfirmed === true;
  }

  /**
   * Помечает указанный день как начатый, если день можно начать ({@link #canStartDay}).
   * @param dayId - Идентификатор дня для начала.
   * @returns Текущий объект Order.
   */
  public startDay(dayId: string) {
    if (this.canStartDay()) {
      this.registry.days = this.registry.days.map((day: RtsDay) => {
        if (day.orderNumber === dayId) {
          return {
            ...day,
            started: true
          };
        }
        return day;
      });
    }
    return this;
  }

  /**
   * Проверяет, что ни один день ещё не был начат.
   * @returns true, если ни один день не помечен как начатый.
   */
  public checkFirstDayStarted() {
    return !this.registry.days.some((day: RtsDay) => day.started);
  }

  /**
   * Завершает указанный день и формирует данные для подписи конца дня.
   * @param dayId - Идентификатор дня для завершения.
   * @returns Объект с обновлённым заказом и данными для запроса подписи конца дня.
   */
  public endDay(dayId: string): {
    order: Order;
    endDayResult?: SignEndOfDayRequest;
  } {
    let result: SignEndOfDayRequest | undefined;

    this.registry.days = this.registry.days.map((day: RtsDay) => {
      const shipmentDate = day.shipmentDate.split(' ')[0];
      if (day.orderNumber === dayId) {
        const completedVehicles = day.vehicles
          .filter(v => !!v.loadingData?.netWeight)
          .map(v => ({
            billOfLandingNumber: v.loadingData.ttnId ?? '',
            billOfLandingDate: shipmentDate,
            vehicleNumber: v.registrationNumber,
            trailerNumber: v.trailerRegistrationNumber ?? '',
            loadingDate: shipmentDate,
            loadedWeightKg: v.loadingData!.netWeight
          }));

        result = {
          signatureDateTime: this.generateSignatureDateTime(),
          // Натуральный ключ: ровно одна расписка на день сделки. Один и тот же ключ уходит и в
          // онлайн-подпись, и в отложенный повтор из очереди — сервер дедуплицирует по нему.
          idempotencyKey: `forwarder_receipt:${this.bargainId}:${dayId}`,
          vehicles: completedVehicles
        };

        return {
          ...day,
          completed: true,
          endDayResult: result
        };
      }
      return day;
    });

    return { order: this, endDayResult: result };
  }

  /**
   * Устанавливает ID документа заметки экспедитора для указанного дня.
   * @param dayId - Идентификатор дня.
   * @param documentId - ID документа заметки.
   * @returns Текущий объект Order.
   */
  public setForwarderNoteId(dayId: string, documentId: number): this {
    let updated: RtsDay | undefined;

    this.registry.days = this.registry.days.map((day: RtsDay) => {
      if (day.orderNumber === dayId) {
        updated = {
          ...day,
          forwarderNoteDocumentId: documentId
        };
        return updated;
      }
      return day;
    });

    return this;
  }

  /**
   * Находит объект RtsDay по идентификатору, убирая символы '|'.
   * @param dayId - Идентификатор дня без символов '|'.
   * @returns RtsDay или null, если не найден.
   */
  public getDayById(dayId: string): RtsDay | null {
    if (this.registry?.days) {
      const day = this.registry.days.find(
        (elm: RtsDay) => Order.isDay(elm, dayId)
      );
      return day || null;
    }

    return null;
  }

  /**
   * Проверяет, можно ли обработать день (заявка в работе и день не завершён).
   * @param dayId - Идентификатор дня.
   * @returns true, если статус IN_WORK и день не завершён.
   */
  public canProcessDay(dayId: string): boolean {
    const day = this.getDayById(dayId);
    return this.status === Status.IN_WORK && !!day && !day.completed;
  }

  /**
   * Ищет транспортное средство по его ID во всех днях или в указанном дне.
   * @param vehicleId - Идентификатор транспортного средства.
   * @param dayId - Опциональный идентификатор дня для поиска в контексте конкретного дня.
   * @returns RtsVehicle или null, если не найден.
   */
  public getVehicleById(vehicleId: string, dayId?: string): RtsVehicle | null {
    if (!this.registry.days) {
      return null;
    }

    if (dayId) {
      // Поиск только в указанном дне
      const day = this.registry.days.find(
        d => Order.isDay(d, dayId)
      );
      if (day) {
        return day.vehicles.find(v => v.id.toString() === vehicleId) || null;
      }
      return null;
    }

    // Старая логика для обратной совместимости
    for (const day of this.registry.days) {
      const vehicle = day.vehicles.find(v => v.id.toString() === vehicleId);
      if (vehicle) {
        return vehicle;
      }
    }
    return null;
  }

  /**
   * Обновляет данные загрузки для указанного транспортного средства в дне.
   * @param dayId - Идентификатор дня.
   * @param vehicleId - Идентификатор транспортного средства.
   * @param ttnId - Номер ТТН.
   * @param loadData - Данные загрузки для установки.
   * @returns Текущий объект Order.
   * @throws Error, если день или транспортное средство не найдены.
   */
  public updateVehicle(
    dayId: string,
    vehicleId: string,
    ttnId: string,
    loadData: RtsLoadingData
  ): this {
    try {
      if (!this.registry?.days) {
        throw new Error('Registry or days data is not defined');
      }

      const day = this.registry.days.find(
        d => Order.isDay(d, dayId)
      );

      if (!day) {
        throw new Error(`Day with ID ${dayId} not found`);
      }

      const vehicle = day.vehicles.find(v => v.id?.toString() === vehicleId?.toString());

      if (!vehicle) {
        throw new Error(
          `Vehicle with ID ${vehicleId} not found in day ${dayId}`
        );
      }

      vehicle.loadingData = {
        netWeight: loadData.netWeight,
        quarantineCertificateNumber: loadData.quarantineCertificateNumber,
        sdizNumber: loadData.sdizNumber,
        quarantineCertificateDate: Number(loadData.quarantineCertificateDate),
        declarationOfConformityDate: Number(
          loadData.declarationOfConformityDate
        ),
        tareWeight: loadData.tareWeight,
        grossWeight: loadData.grossWeight,

        declarationOfConformityNumber: loadData.declarationOfConformityNumber,
        lastUpdate: Date.now(),
        ttnId
      };

      return this;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Добавляет новое транспортное средство в указанный день.
   * @param dayId - Идентификатор дня.
   * @param newVehicle - Объект транспортного средства для добавления.
   * @returns Текущий объект Order.
   */
  public addVehicle(dayId: string, newVehicle: RtsVehicle) {
    const day = this.registry.days.find(
      d => Order.isDay(d, dayId)
    );
    if (day) {
      day.vehicles.push(newVehicle);
    }
    return this;
  }

  /**
   * Редактирует базовые данные транспортного средства в указанном дне.
   * Обновляет любые поля из Partial<RtsVehicle>, кроме `id` и `loadingData`.
   * Для `trailerRegistrationNumber` автоматически синхронизирует `hasTrailer`.
   * @param dayId - Идентификатор дня.
   * @param vehicleId - Идентификатор транспортного средства.
   * @param editVehicle - Частичные данные для обновления.
   * @returns Текущий объект Order.
   */
  public editVehiclesBaseData(
    dayId: string,
    vehicleId: string,
    editVehicle: Partial<Omit<RtsVehicle, 'id' | 'loadingData'>>
  ): this {
    try {
      const day = this.registry?.days?.find(
        d => Order.isDay(d, dayId)
      );
      if (!day) {
        Sentry.captureMessage(`Day with ID ${dayId} not found in editVehiclesBaseData`, {
          level: 'warning',
          extra: { dayId, vehicleId }
        });
        return this;
      }

      const vehicle = day.vehicles?.find(v => v.id?.toString() === vehicleId?.toString());
      if (!vehicle) {
        Sentry.captureMessage(`Vehicle with ID ${vehicleId} not found in day ${dayId}`, {
          level: 'warning',
          extra: { dayId, vehicleId }
        });
        return this;
      }

      // Универсальное обновление по ключам. RtsVehicle не имеет индексной сигнатуры, поэтому для
      // динамической записи по ключу используем изменяемое представление как Record.
      const mutableVehicle = vehicle as unknown as Record<string, unknown>;
      Object.entries(editVehicle ?? {}).forEach(([key, value]) => {
        if (value === undefined) return; // пропускаем пустые значения
        if (key === 'id' || key === 'loadingData') return; // защита от критичных полей

        if (key === 'trailerRegistrationNumber') {
          mutableVehicle['trailerRegistrationNumber'] = value;
          const hasTrailer = typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
          mutableVehicle['hasTrailer'] = hasTrailer;
          return;
        }

        // Прочие поля копируем как есть
        mutableVehicle[key] = value;
      });

      return this;
    } catch (error) {
      Sentry.captureException(error, { extra: { operation: 'editVehiclesBaseData', dayId, vehicleId } });
      return this;
    }
  }

  /**
   * Логирует событие генерации ТТН.
   * @param data - Массив CellData для параметров лога.
   */
  public generateTtnActions(data: CellData[]) {
    if (this.status === Status.IN_WORK) {
      if (!this.log) {
        this.log = [];
      }

      const logObj: ActionLog = {
        action: 'ttn_generation',
        datetime: Date.now().toString(),
        parameters: {}
      };

      data.forEach((el: CellData) => {
        logObj.parameters[el.name] = el.value;
      });

      this.log.push(logObj);
    }
  }

  /**
   * Логирует событие генерации TRN.
   * @param data - Массив CellData для параметров лога.
   */
  public generateTrnActions(data: CellData[]) {
    if (this.status === Status.IN_WORK) {
      if (!this.log) {
        this.log = [];
      }

      const logObj: ActionLog = {
        action: 'trn_generation',
        datetime: Date.now().toString(),
        parameters: {}
      };

      data.forEach((el: CellData) => {
        logObj.parameters[el.name] = el.value;
      });

      this.log.push(logObj);
    }
  }

  /**
   * Логирует событие генерации отчёта экспедитора.
   * @param data - Массив CellData для параметров лога.
   */
  public generateForwarderReportActions(data: CellData[]) {
    if (!this.log) {
      this.log = [];
    }

    const logObj: ActionLog = {
      action: 'forwarderReport_generation',
      datetime: Date.now().toString(),
      parameters: {}
    };

    data.forEach((el: CellData) => {
      logObj.parameters[el.name] = el.value;
    });

    this.log.push(logObj);
  }

  /**
   * Логирует событие генерации заметки экспедитора.
   * @param data - Массив CellData для параметров лога.
   */
  public generateForwarderNoteActions(data: CellData[]) {
    if (this.status === Status.IN_WORK) {
      if (!this.log) {
        this.log = [];
      }

      const logObj: ActionLog = {
        action: 'forwarderNote_generation',
        datetime: Date.now().toString(),
        parameters: {}
      };

      data.forEach((el: CellData) => {
        logObj.parameters[el.name] = el.value;
      });

      this.log.push(logObj);
    }
  }

  /**
   * Логирует событие генерации RTS.
   * @param data - Массив CellData для параметров лога.
   */
  public generateRTSActions(data: CellData[]) {
    if (this.status === Status.ACCEPTED || this.status === Status.IN_WORK) {
      if (!this.log) {
        this.log = [];
      }

      const logObj: ActionLog = {
        action: 'RTS_generation',
        datetime: Date.now().toString(),
        parameters: {}
      };

      data.forEach((el: CellData) => {
        logObj.parameters[el.name] = el.value;
      });

      this.log.push(logObj);
    }
  }

  /**
   * Проверяет, что все дни завершены и заявка в работе.
   * @returns true, если все дни завершены и статус IN_WORK.
   */
  public checkCompletedStatus(): boolean {
    if (this?.registry?.days && this?.registry?.days.length) {
      return (
        this.registry.days.every(day => day.completed) &&
        this.status === Status.IN_WORK
      );
    }
    return false;
  }

  /**
   * Проверяет, что хотя бы один день завершён и заявка в работе.
   * @returns true, если есть хотя бы один завершённый день и статус IN_WORK.
   */
  public checkCompletedDayExist(): boolean {
    if (this?.registry?.days && this?.registry?.days.length) {
      return (
        this.registry.days.some(day => day.completed) &&
        this.status === Status.IN_WORK
      );
    }
    return false;
  }

  /**
   * Проверяет, архивен ли статус заявки (COMPLETED или CLOSED).
   * @returns true, если статус COMPLETED или CLOSED.
   */
  public statusIsArchive() {
    return this.status === Status.COMPLETED || this.status === Status.CLOSED;
  }

  /**
   * Вычисляет общий нетто вес по всем завершённым дням.
   * @returns Сумму нетто весов всех завершённых дней.
   */
  public getTotalNetWeight() {
    let totalWeight = 0;
    if (this.registry && this.registry.days) {
      this.registry.days.forEach(day => {
        if (day.completed) {
          day.vehicles.forEach(car => {
            if (car?.loadingData?.netWeight) {
              totalWeight += car.loadingData.netWeight;
            }
          });
        }
      });
    }
    return totalWeight;
  }

  /**
   * Плановый («назначенный») вес: по 25 т на каждую машину. ВНИМАНИЕ: суммирует по ВСЕМ машинам всех
   * дней реестра, БЕЗ фильтра по завершённости дня — в отличие от {@link Order#getTotalNetWeight},
   * который учитывает только завершённые дни.
   * @returns Общий назначенный вес.
   */
  public getTotalAssignedWeight() {
    let totalWeight = 0;
    if (this.registry && this.registry.days) {
      this.registry.days.forEach(day => {
        day.vehicles.forEach(() => {
          totalWeight += 25;
        });
      });
    }
    return totalWeight;
  }

  /**
   * Получает порядковый номер дня (1-based) по его идентификатору.
   * @param dayId - Идентификатор дня.
   * @returns Порядковый номер дня или 1, если не найден.
   */
  public getDayNumberById(dayId: string) {
    if (this.registry?.days) {
      return this.registry.days.findIndex(el => el.orderNumber === dayId) + 1;
    }
    return 1;
  }

  /**
   * Помечает заявку как завершённую.
   */
  public completeOrder() {
    this.status = Status.COMPLETED;
  }

  /**
   * Генерирует текущую дату-время в формате ISO-Zoned с часовым поясом Москвы.
   * @returns Отформатированную строку ISO-Zoned даты-времени.
   */
  private generateSignatureDateTime(): IsoZonedDateTime {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());
    const tzOffset = -now.getTimezoneOffset();
    const tzHours = Math.floor(Math.abs(tzOffset) / 60);
    const tzMinutes = Math.abs(tzOffset) % 60;
    const sign = tzOffset >= 0 ? '+' : '-';
    const formattedOffset = `${sign}${pad(tzHours)}:${pad(tzMinutes)}`;
    const region = 'Europe/Moscow';
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${formattedOffset}[${region}]`;
  }
}
export interface OrdersDataDto {
  requests: Order[];
  total: number;
  currentPageSize: number;
}

export interface OrdersResultDto {
  data: OrdersDataDto;
  success: boolean;
}

export interface SignExpeditorNoteResultDto {
  data: number;
  success: boolean;
}

export interface OrderByIdResultDto {
  data: Order;
  success: boolean;
}

export interface OrderResultDto {
  data: Order;
  success: boolean;
}
